import { gsap } from "gsap";
import { useId, useLayoutEffect, useRef } from "react";

import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { findGradient } from "../../constants/cardGradients";
import { COLOCA_WATERMARK_SVG_DATA_URL } from "../../constants/cardWatermark";

/** The organization's designs the secure view cycles through, and the standard one the phone keeps. */
const SKIN_GRADIENT_IDS = ["oceano", "magma", "jade"] as const;
const STANDARD_GRADIENT_ID = "coloca";

interface GradientStop {
  color: string;
  offset: string;
}

/** The colour stops of a catalog gradient (`linear-gradient(135deg, #2563eb 0%, …)`), for an SVG gradient. */
function gradientStops(id: string): GradientStop[] {
  const css = findGradient(id).css;
  return Array.from(css.matchAll(/(#[0-9a-f]{3,8}|oklch\([^)]*\))\s+(\d+%)/gi), ([, color, offset]) => ({
    color,
    offset,
  }));
}

const SKINS = SKIN_GRADIENT_IDS.map(gradientStops);
const STANDARD = gradientStops(STANDARD_GRADIENT_ID);

/**
 * Where an organization's card design shows: the API opens the secure card view, whose card takes the
 * organization's colours and logo next to the Coloca mark; the phone wallet keeps the standard card. The one
 * thing that loops is the secure view's card changing colour while the phone's stays put — that contrast is
 * the message. Static (and complete) with reduced motion.
 */
export function CardBrandingDiagram({ className }: { className?: string }) {
  const { t } = useTranslations("cards");
  const ref = useRef<SVGSVGElement>(null);
  const reduced = usePrefersReducedMotion();
  const uid = useId().replace(/:/g, "");

  useLayoutEffect(() => {
    const svg = ref.current;
    if (!svg || reduced) return;
    const ctx = gsap.context(() => {
      const api = svg.querySelector<SVGGElement>("[data-api]");
      const link = svg.querySelector<SVGPathElement>("[data-link]");
      const view = svg.querySelector<SVGGElement>("[data-view]");
      const logo = svg.querySelector<SVGGElement>("[data-logo]");
      const phone = svg.querySelector<SVGGElement>("[data-phone]");
      const skins = Array.from(svg.querySelectorAll<SVGRectElement>("[data-skin]"));

      const intro = gsap.timeline();
      intro.fromTo(
        api,
        { opacity: 0, x: -6 },
        { opacity: 1, x: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
      );
      if (link) {
        const length = link.getTotalLength();
        intro.fromTo(
          link,
          { strokeDasharray: length, strokeDashoffset: length },
          { strokeDashoffset: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
          0.2,
        );
      }
      intro.fromTo(
        view,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
        0.3,
      );
      // The loud moment: the organization's logo lands on the card, next to the Coloca mark.
      intro.fromTo(
        logo,
        { scale: 0.4, opacity: 0, transformOrigin: "50% 50%" },
        { scale: 1, opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.pop },
        0.75,
      );
      intro.fromTo(
        phone,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
        0.3 + MOTION_STAGGER.loose * 4,
      );

      // The secure view's card goes through the organization's designs; the phone's never changes.
      const [, second, third] = skins;
      if (second && third) {
        gsap
          .timeline({ repeat: -1, delay: 2 })
          .to(second, { opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.enter }, "+=1.2")
          .to(third, { opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.enter }, "+=1.6")
          .set(second, { opacity: 0 }, "+=1.6")
          .to(third, { opacity: 0, duration: MOTION_DURATION.slow, ease: MOTION_EASE.enter });
      }
    }, svg);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <svg
      ref={ref}
      viewBox="0 0 340 152"
      role="img"
      aria-label={t("branding_diagram_aria")}
      className={cn("h-auto w-full", className)}
    >
      <defs>
        {SKINS.map((stops, index) => (
          <linearGradient key={`skin-${index}`} id={`${uid}-skin-${index}`} x1="0" y1="0" x2="1" y2="1">
            {stops.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
        ))}
        <linearGradient id={`${uid}-standard`} x1="0" y1="0" x2="1" y2="1">
          {STANDARD.map((stop) => (
            <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>

      {/* The API asks for the secure view. */}
      <g data-api>
        <rect x="6" y="62" width="38" height="20" rx="10" className="fill-primary/10 stroke-primary/40" />
        <text
          x="25"
          y="75.5"
          textAnchor="middle"
          className="fill-primary font-mono text-[9px] font-semibold tracking-wider"
        >
          {t("branding_diagram_api")}
        </text>
      </g>
      <path
        data-link
        d="M44 72 C 54 72, 60 72, 70 72"
        fill="none"
        className="stroke-primary/50"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* The secure card view: a window with the organization's card in it. */}
      <g data-view>
        <rect x="70" y="16" width="134" height="112" rx="10" className="fill-card stroke-border" />
        <line x1="70" y1="31" x2="204" y2="31" className="stroke-border" />
        {[80, 88, 96].map((cx) => (
          <circle key={cx} cx={cx} cy="23.5" r="2" className="fill-muted-foreground/35" />
        ))}
        {SKINS.map((_, index) => (
          <rect
            key={`card-${index}`}
            data-skin
            x="83"
            y="44"
            width="108"
            height="68"
            rx="7"
            fill={`url(#${uid}-skin-${index})`}
            opacity={index === 0 ? 1 : 0}
          />
        ))}
        <image href={COLOCA_WATERMARK_SVG_DATA_URL} x="90" y="51" width="13" height="13" />
        <rect x="91" y="93" width="54" height="3.5" rx="1.75" fill="white" fillOpacity="0.65" />
        <rect x="91" y="101" width="32" height="3.5" rx="1.75" fill="white" fillOpacity="0.4" />
        <text
          x="137"
          y="143"
          textAnchor="middle"
          className="fill-muted-foreground font-sans text-[9px] font-medium"
        >
          {t("branding_diagram_secure_view")}
        </text>
      </g>
      {/* The organization's logo, a placeholder shape: the design belongs to whoever uploads it. */}
      <g data-logo>
        <rect
          x="161"
          y="50"
          width="22"
          height="15"
          rx="3.5"
          fill="white"
          fillOpacity="0.16"
          stroke="white"
          strokeOpacity="0.75"
          strokeDasharray="2.5 2"
        />
        <circle cx="168" cy="57.5" r="2.6" fill="white" fillOpacity="0.85" />
        <rect x="172.5" y="55.2" width="6.5" height="4.6" rx="1.2" fill="white" fillOpacity="0.6" />
      </g>

      <line x1="222" y1="22" x2="222" y2="124" className="stroke-border" strokeDasharray="3 4" />

      {/* The phone wallet: the tokenized card keeps the standard design. */}
      <g data-phone>
        <rect x="246" y="10" width="68" height="120" rx="13" className="fill-card stroke-border" />
        <rect x="270" y="16" width="20" height="4" rx="2" className="fill-muted-foreground/30" />
        <rect x="253" y="32" width="54" height="34" rx="4.5" fill={`url(#${uid}-standard)`} />
        <image href={COLOCA_WATERMARK_SVG_DATA_URL} x="257" y="36" width="8" height="8" />
        <rect x="253" y="76" width="54" height="9" rx="3" className="fill-muted" />
        <rect x="253" y="90" width="54" height="9" rx="3" className="fill-muted" />
        <rect x="253" y="104" width="36" height="9" rx="3" className="fill-muted" />
        <text
          x="280"
          y="143"
          textAnchor="middle"
          className="fill-muted-foreground font-sans text-[9px] font-medium"
        >
          {t("branding_diagram_wallets")}
        </text>
      </g>
    </svg>
  );
}
